import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ShieldCheck, ShieldAlert, Shield, AlertTriangle, Clock, Users, CheckCircle, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import BlockedStaffPanel from '../components/BlockedStaffPanel';

const JOB_ROLES_LIST = ['Sales Girl', 'Sales Man', 'Driver', 'P.R.O', 'H.R', 'Assistant Manager', 'Floor Manager', 'House Keeping', 'Packing Staff'];

const StatusIcon = ({ status }) => {
  if (status === 'green') return <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />;
  if (status === 'yellow') return <span className="w-2.5 h-2.5 rounded-full bg-amber-500 inline-block" />;
  return <span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" />;
};

export default function CompliancePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [salesStatus, setSalesStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [benchmarks, setBenchmarks] = useState([]);
  const [branches, setBranches] = useState([]);
  const [benchDialog, setBenchDialog] = useState(false);
  const [benchForm, setBenchForm] = useState({ branch_id: '', job_role: '', max_staff: '' });

  const isSuperAdmin = user?.system_role === 'super_admin';

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const promises = [
          api.get('/compliance/daily'),
          api.get('/attendance/sales-status')
        ];
        if (isSuperAdmin) {
          promises.push(api.get('/staff-benchmarks'));
          promises.push(api.get('/branches'));
        }
        const results = await Promise.all(promises);
        setData(results[0].data);
        setSalesStatus(results[1].data);
        if (isSuperAdmin) {
          setBenchmarks(results[2].data);
          setBranches(results[3].data);
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, [isSuperAdmin]);

  const refreshBenchmarks = async () => {
    const res = await api.get('/staff-benchmarks');
    setBenchmarks(res.data);
  };

  const handleSaveBenchmark = async () => {
    if (!benchForm.branch_id || !benchForm.job_role || !benchForm.max_staff) {
      toast.error('Fill all fields'); return;
    }
    try {
      await api.post('/staff-benchmarks', {
        branch_id: benchForm.branch_id,
        job_role: benchForm.job_role,
        max_staff: parseInt(benchForm.max_staff)
      });
      toast.success('Benchmark saved');
      setBenchDialog(false);
      setBenchForm({ branch_id: '', job_role: '', max_staff: '' });
      refreshBenchmarks();
    } catch (err) { toast.error('Failed to save'); }
  };

  const handleDeleteBenchmark = async (id) => {
    if (!window.confirm('Delete this benchmark?')) return;
    try {
      await api.delete(`/staff-benchmarks/${id}`);
      toast.success('Deleted');
      refreshBenchmarks();
    } catch { toast.error('Failed'); }
  };

  const handleBulkApprove = async () => {
    try {
      const pendingEntries = await api.get('/achievements', { params: { status: 'pending' } });
      const ids = pendingEntries.data.map(e => e.id);
      if (ids.length === 0) { toast.info('No pending entries to approve'); return; }
      const res = await api.post('/achievements/bulk-approve', { ids });
      toast.success(`Approved ${res.data.approved} entries`);
      // Refresh
      const [compRes, statusRes] = await Promise.all([api.get('/compliance/daily'), api.get('/attendance/sales-status')]);
      setData(compRes.data);
      setSalesStatus(statusRes.data);
    } catch (err) { toast.error('Failed to bulk approve'); }
  };

  if (loading) return <div className="space-y-4">{[1,2,3,4].map(i => <div key={i} className="h-24 bg-neutral-200 rounded animate-pulse" />)}</div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="compliance-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">Daily Compliance</h1>
          <p className="text-neutral-500 text-sm mt-1">Real-time staff accountability panel &middot; {data?.date}</p>
        </div>
        <Button className="bg-red-600 hover:bg-red-700 text-white rounded-sm" onClick={handleBulkApprove} data-testid="bulk-approve-all-button">
          <CheckCircle className="h-4 w-4 mr-2" /> Bulk Approve All Pending
        </Button>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border border-red-200 shadow-sm rounded-md bg-red-50/50">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <ShieldAlert className="h-6 w-6 text-red-600" />
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-red-500">Locked</p>
                <p className="text-2xl font-heading font-black text-red-700">{data?.locked_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-amber-200 shadow-sm rounded-md bg-amber-50/50">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <Shield className="h-6 w-6 text-amber-600" />
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-amber-500">At Risk</p>
                <p className="text-2xl font-heading font-black text-amber-700">{data?.at_risk_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-neutral-200 shadow-sm rounded-md">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-orange-500" />
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">No Submission</p>
                <p className="text-2xl font-heading font-black text-neutral-900">{data?.no_submission_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-neutral-200 shadow-sm rounded-md">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <Clock className="h-6 w-6 text-purple-500" />
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Stale Approvals</p>
                <p className="text-2xl font-heading font-black text-neutral-900">{data?.stale_approvals_count}</p>
                <p className="text-[10px] text-neutral-400">pending &gt;24hrs</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stale Approvals Warning */}
      {data?.stale_approvals_count > 0 && (
        <Card className="border border-purple-200 bg-purple-50/50 shadow-sm rounded-md">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-purple-600 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-purple-800">Approvals Pending &gt; 24 Hours</p>
                <p className="text-xs text-purple-600 mt-1">{data.stale_approvals_count} entries are awaiting manager approval for more than 24 hours. Please review immediately.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Blocked Staff — every gated employee (not just sales), with unblock action */}
      <BlockedStaffPanel canUnblock={user?.system_role === 'super_admin' || user?.system_role === 'management'} />

      {/* Sales Staff Status Table */}
      <Card className="border border-neutral-200 shadow-sm rounded-md overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold text-neutral-700 flex items-center gap-2">
              <Users className="h-4 w-4" /> Sales Staff Status ({salesStatus.length} staff)
            </CardTitle>
            <div className="flex items-center gap-3 text-xs text-neutral-500">
              <span className="flex items-center gap-1"><StatusIcon status="green" /> Eligible</span>
              <span className="flex items-center gap-1"><StatusIcon status="yellow" /> Warning</span>
              <span className="flex items-center gap-1"><StatusIcon status="red" /> Locked</span>
            </div>
          </div>
        </CardHeader>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50">
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Status</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Name</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Role</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 hidden sm:table-cell">Days Missing</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 hidden md:table-cell">Reason</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 text-right">Profile</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {salesStatus.map(s => (
                <TableRow key={s.id} className={`hover:bg-neutral-50 ${s.status === 'red' ? 'bg-red-50/30' : s.status === 'yellow' ? 'bg-amber-50/30' : ''}`}
                  data-testid={`compliance-row-${s.id}`}>
                  <TableCell><StatusIcon status={s.status} /></TableCell>
                  <TableCell className="text-sm font-medium text-neutral-900">{s.name}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="text-xs bg-red-50 text-red-700">{s.job_role}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-neutral-600 hidden sm:table-cell">{s.days_missing}</TableCell>
                  <TableCell className="text-xs text-neutral-500 max-w-[250px] truncate hidden md:table-cell">{s.message || '-'}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" className="text-xs text-red-600 hover:text-red-700"
                      onClick={() => navigate(`/employees/${s.id}/profile`)} data-testid={`view-profile-${s.id}`}>
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Staff Not Submitted Today */}
      {data?.no_submission?.length > 0 && (
        <Card className="border border-neutral-200 shadow-sm rounded-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-neutral-700 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-500" /> Staff Who Did NOT Submit Achievement Today
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-wrap gap-2">
              {data.no_submission.map(s => (
                <Badge key={s.id} variant="secondary" className="text-xs py-1.5 px-3 cursor-pointer hover:bg-neutral-200"
                  onClick={() => navigate(`/employees/${s.id}/profile`)}>
                  {s.name}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Staff Benchmarks - Super Admin Only */}
      {isSuperAdmin && (
        <Card className="border border-neutral-200 shadow-sm rounded-md" data-testid="staff-benchmarks-card">
          <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-sm font-semibold text-neutral-700 flex items-center gap-2">
                <Users className="h-4 w-4 text-red-600" /> Staff Benchmarks
              </CardTitle>
              <p className="text-xs text-neutral-500 mt-0.5">Set per-branch & per-role staff limits. Over/Under staffing flagged automatically.</p>
            </div>
            <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white rounded-sm" onClick={() => setBenchDialog(true)} data-testid="add-benchmark-button">
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            {benchmarks.length === 0 ? (
              <p className="text-sm text-neutral-500 text-center py-8">No benchmarks set. Click "Add" to define limits per branch and role.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-neutral-50">
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Branch</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Job Role</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Limit</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Current</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Status</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {benchmarks.map(b => (
                      <TableRow key={b.id} data-testid={`benchmark-row-${b.id}`}>
                        <TableCell className="text-sm font-medium">{b.branch_name}</TableCell>
                        <TableCell className="text-sm text-neutral-700">{b.job_role}</TableCell>
                        <TableCell className="text-sm text-neutral-700">{b.max_staff}</TableCell>
                        <TableCell className="text-sm font-semibold">{b.current_staff}</TableCell>
                        <TableCell>
                          {b.status === 'over' && <Badge className="text-xs bg-red-100 text-red-700">Over Staffing (+{b.current_staff - b.max_staff})</Badge>}
                          {b.status === 'under' && <Badge className="text-xs bg-amber-100 text-amber-700">Under Staffing (-{b.max_staff - b.current_staff})</Badge>}
                          {b.status === 'ok' && <Badge className="text-xs bg-emerald-100 text-emerald-700">On Target</Badge>}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDeleteBenchmark(b.id)} data-testid={`delete-benchmark-${b.id}`}>
                            <Trash2 className="h-3.5 w-3.5 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Benchmark Dialog */}
      <Dialog open={benchDialog} onOpenChange={setBenchDialog}>
        <DialogContent className="max-w-sm" data-testid="benchmark-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Add Staff Benchmark</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Branch</Label>
              <Select value={benchForm.branch_id} onValueChange={v => setBenchForm(p => ({ ...p, branch_id: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="bench-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Job Role</Label>
              <Select value={benchForm.job_role} onValueChange={v => setBenchForm(p => ({ ...p, job_role: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="bench-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>{JOB_ROLES_LIST.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Max Staff Count</Label>
              <Input type="number" value={benchForm.max_staff} onChange={e => setBenchForm(p => ({ ...p, max_staff: e.target.value }))}
                className="rounded-sm" data-testid="bench-max-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBenchDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSaveBenchmark} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="bench-save-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
